import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateSwotFactorRequest,
  GrowthJourneyService,
  SwotFactor,
  UpdateSwotFactorRequest
} from './growth-journey.service';

const SWOT_FACTOR_TYPES = [
  'InternalStrength',
  'InternalWeakness',
  'ExternalOpportunity',
  'ExternalThreat'
] as const;

type SwotFactorType = (typeof SWOT_FACTOR_TYPES)[number];

interface SwotFactorRow extends SwotFactor {
  localDraft?: boolean;
  saving?: boolean;
  toggling?: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-swot-factor-catalog',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    ButtonModule,
    TableModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    TagModule
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.journey.swot.factors.title')"
      [description]="t('growth.journey.swot.factors.description')">
      <div class="toolbar">
        <a pButton routerLink="/growth/journey/discoveries" severity="secondary" [label]="t('common.back')"></a>
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.swot.factors.help.title'"
        [descriptionKey]="'growth.journey.swot.factors.help.description'" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        @for (factorType of factorTypes; track factorType) {
          <section class="card">
            <div class="section-header">
              <h3>{{ t(factorTypeKey(factorType)) }}</h3>
              <button
                pButton
                type="button"
                size="small"
                [label]="t('growth.journey.swot.factors.add')"
                (click)="addDraft(factorType)"></button>
            </div>

            <p-table [value]="rowsForType(factorType)" styleClass="factor-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.journey.swot.factors.columns.title') }}</th>
                  <th>{{ t('growth.journey.swot.factors.columns.description') }}</th>
                  <th>{{ t('growth.journey.swot.factors.columns.weight') }}</th>
                  <th>{{ t('growth.journey.swot.factors.columns.order') }}</th>
                  <th>{{ t('growth.journey.swot.factors.columns.active') }}</th>
                  <th>{{ t('growth.journey.swot.factors.columns.source') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <input pInputText class="text-input" [(ngModel)]="row.title" />
                  </td>
                  <td>
                    <input pInputText class="text-input" [(ngModel)]="row.description" />
                  </td>
                  <td>
                    <p-inputNumber
                      [(ngModel)]="row.defaultRelativeWeight"
                      [min]="0"
                      [max]="1"
                      [step]="0.05" />
                  </td>
                  <td>
                    <p-inputNumber [(ngModel)]="row.displayOrder" [min]="0" [step]="1" />
                  </td>
                  <td>
                    <p-checkbox [(ngModel)]="row.isActive" [binary]="true" [disabled]="row.localDraft" />
                  </td>
                  <td>
                    @if (!row.localDraft) {
                      <p-tag [value]="sourceLabel(row.source)" [severity]="sourceSeverity(row.source)" />
                    }
                  </td>
                  <td class="actions">
                    <button
                      pButton
                      type="button"
                      size="small"
                      [label]="t('common.save')"
                      [loading]="row.saving"
                      (click)="saveRow(row)"></button>
                    @if (!row.localDraft) {
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="secondary"
                        [label]="row.isActive ? t('growth.journey.swot.factors.actions.deactivate') : t('growth.journey.swot.factors.actions.activate')"
                        [loading]="row.toggling"
                        (click)="toggleActive(row)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
      }

      @if (actionError()) {
        <p class="action-error">{{ actionError() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; }
    .section-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-header h3 { margin: 0; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .text-input { width: 100%; min-width: 120px; }
    .action-error { margin: 0; color: #b91c1c; }
    :host ::ng-deep .factor-table .p-inputnumber { width: 100%; max-width: 100px; }
  `]
})
export class GrowthSwotFactorCatalogComponent implements OnInit {
  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly factorTypes = SWOT_FACTOR_TYPES;
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly rows = signal<SwotFactorRow[]>([]);

  private draftCounter = 0;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);

    this.journey.listSwotFactors(true).subscribe({
      next: factors => {
        this.rows.set(factors.map(factor => ({ ...factor })));
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(this.resolveLoadError(err));
      }
    });
  }

  rowsForType(factorType: SwotFactorType): SwotFactorRow[] {
    return this.rows().filter(row => row.factorType === factorType);
  }

  factorTypeKey(factorType: SwotFactorType): string {
    const map: Record<SwotFactorType, string> = {
      InternalStrength: 'growth.journey.swot.factorType.internalStrength',
      InternalWeakness: 'growth.journey.swot.factorType.internalWeakness',
      ExternalOpportunity: 'growth.journey.swot.factorType.externalOpportunity',
      ExternalThreat: 'growth.journey.swot.factorType.externalThreat'
    };
    return map[factorType];
  }

  sourceLabel(source: string): string {
    switch (source) {
      case 'SystemDefault':
        return this.t('growth.journey.swot.factors.source.systemDefault');
      case 'TenantCustom':
        return this.t('growth.journey.swot.factors.source.tenantCustom');
      case 'AiProposed':
        return this.t('growth.journey.swot.factors.source.aiProposed');
      default:
        return source;
    }
  }

  sourceSeverity(source: string): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    if (source === 'AiProposed') {
      return 'warn';
    }

    if (source === 'TenantCustom') {
      return 'info';
    }

    return 'secondary';
  }

  addDraft(factorType: SwotFactorType): void {
    this.draftCounter += 1;
    const draft: SwotFactorRow = {
      id: `draft-${this.draftCounter}`,
      title: '',
      description: '',
      factorType,
      defaultRelativeWeight: 0.25,
      displayOrder: this.nextDisplayOrder(factorType),
      isActive: true,
      source: 'TenantCustom',
      locale: 'fa-IR',
      localDraft: true
    };

    this.rows.set([...this.rows(), draft]);
  }

  saveRow(row: SwotFactorRow): void {
    if (!row.title?.trim()) {
      this.actionError.set(this.t('growth.journey.swot.factors.errors.titleRequired'));
      return;
    }

    row.saving = true;
    this.actionError.set(null);

    if (row.localDraft) {
      const body: CreateSwotFactorRequest = {
        title: row.title.trim(),
        description: row.description?.trim() || null,
        factorType: row.factorType,
        defaultRelativeWeight: row.defaultRelativeWeight,
        displayOrder: row.displayOrder,
        locale: row.locale || 'fa-IR'
      };

      this.journey.createSwotFactor(body).subscribe({
        next: () => {
          row.saving = false;
          this.messages.add({ severity: 'success', summary: this.t('growth.journey.swot.factors.saved') });
          this.load();
        },
        error: err => {
          row.saving = false;
          this.actionError.set(this.resolveActionError(err));
        }
      });
      return;
    }

    const body: UpdateSwotFactorRequest = {
      title: row.title.trim(),
      description: row.description?.trim() || null,
      factorType: row.factorType,
      defaultRelativeWeight: row.defaultRelativeWeight,
      displayOrder: row.displayOrder
    };

    this.journey.updateSwotFactor(row.id, body).subscribe({
      next: () => {
        row.saving = false;
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.swot.factors.saved') });
        this.load();
      },
      error: err => {
        row.saving = false;
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  toggleActive(row: SwotFactorRow): void {
    row.toggling = true;
    this.actionError.set(null);

    this.journey.setSwotFactorActive(row.id, !row.isActive).subscribe({
      next: () => {
        row.toggling = false;
        this.load();
      },
      error: err => {
        row.toggling = false;
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private nextDisplayOrder(factorType: SwotFactorType): number {
    const existing = this.rowsForType(factorType);
    if (existing.length === 0) {
      return 1;
    }

    return Math.max(...existing.map(row => row.displayOrder ?? 0)) + 1;
  }

  private resolveLoadError(error: unknown): string {
    return this.t('common.loadFailed');
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_swot_factor_not_found') {
      return this.t('growth.journey.swot.factors.errors.notFound');
    }

    if (code === 'growth_swot_factor_duplicate') {
      return this.t('growth.journey.swot.factors.errors.duplicate');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
